import type { AssembledContract, Block } from "../contracts/types";
import { amountToWords } from "../contracts/amountToWords";
import { CERT_KIND_LABEL, CONTRACT_KIND_LABEL, contractTotal, type PaymentProject } from "./types";

function money(amount: number, currency: string): string {
  if (amount == null || !isFinite(amount)) return `${currency} —`;
  return `${currency} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** A statement of all payment certificates authorised to one payee/supplier
 *  across a project (all their contracts), with subtotals and a grand total. */
export function assembleSupplierStatement(project: PaymentProject, supplier: string): AssembledContract {
  const contracts = project.contracts.filter((c) => (c.supplier || c.party || "—").trim() === supplier);
  const currency = contracts[0]?.currency || project.currency;
  const grand = contracts.reduce((s, c) => s + contractTotal(c), 0);
  const certCount = contracts.reduce((s, c) => s + c.certificates.length, 0);

  const blocks: Block[] = [
    { type: "heading", text: "Payment Statement", level: 1 },
    {
      type: "table",
      columns: ["Field", "Detail"],
      rows: [
        ["Project", `${project.name}${project.reference ? ` (${project.reference})` : ""}`],
        ["Payee / Supplier", supplier],
        ["Contracts", String(contracts.length)],
        ["Certificates", String(certCount)],
        ["Total authorised", money(grand, currency)],
        ["Statement date", project.contracts[0]?.certificates[0]?.date || ""],
      ],
    },
  ];

  for (const c of contracts) {
    blocks.push({ type: "heading", text: `${c.name} — ${CONTRACT_KIND_LABEL[c.kind]}`, level: 2 });
    const certs = [...c.certificates].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    blocks.push({
      type: "table",
      columns: ["Date", "Cert No.", "Type", "Description / Invoice", `Amount (${c.currency || currency})`],
      rows: certs.length
        ? [
            ...certs.map((ct) => [
              ct.date || "—",
              ct.number || "—",
              CERT_KIND_LABEL[ct.kind],
              `${ct.description || "—"}${ct.invoiceRef ? ` · inv ${ct.invoiceRef}` : ""}`,
              money(ct.amount, ct.currency || currency),
            ]),
            ["", "", "", "Subtotal for this contract", money(contractTotal(c), c.currency || currency)],
          ]
        : [["—", "—", "—", "No certificates", money(0, c.currency || currency)]],
    });
  }

  blocks.push({ type: "heading", text: "Total authorised to date", level: 2 });
  blocks.push({ type: "paragraph", text: `The total amount authorised to ${supplier} under this Project to date is ${amountToWords({ amount: grand, currency }) ? amountToWords({ amount: grand, currency }) + " — " : ""}${money(grand, currency)}, across ${contracts.length} contract(s) and ${certCount} certificate(s).` });

  return {
    title: `${project.name} — Payment Statement — ${supplier}`,
    standardLabel: "Payment Statement",
    masterCover: {
      kicker: "Payment Statement",
      title: supplier,
      subtitle: project.name,
      contractLabel: "PAYMENT STATEMENT",
      standardLine: project.reference || "",
      meta: [
        { label: "Project", value: project.name },
        { label: "Payee / Supplier", value: supplier },
        { label: "Contracts", value: String(contracts.length) },
        { label: "Total authorised", value: money(grand, currency) },
      ],
    },
    parts: [{ id: "statement", name: "Payment Statement", cover: { kicker: "Statement", title: "Payment Statement", subtitle: supplier }, blocks }],
    notice: "Payment statement — a summary of payment certificates authorised to the named payee under the Project to the date shown. Amounts exclusive of taxes unless stated.",
  };
}
